// Design tokens — typed constants mirroring the Tailwind config.
// Used by non-Tailwind contexts: charts, react-email templates, canvas, etc.
// Source of truth: ARCHITECTURE.md §17.2, §17.3, §17.5, §17.6
//
// Key contrast ratios (WCAG AA ≥ 4.5:1 for normal text, ≥ 3:1 for large text):
//   neutral-900 on neutral-0:   17.1:1 ✓  (body text)
//   neutral-900 on neutral-50:  16.3:1 ✓  (body on page bg)
//   neutral-700 on neutral-0:    8.9:1 ✓  (secondary text / links)
//   neutral-500 on neutral-0:    4.3:1 ~  (muted text — meets AA-large)
//   primary-600 on neutral-0:    7.4:1 ✓  (CTA buttons text-on-bg)
//   white on primary-600:        7.4:1 ✓  (CTA button text)
//   error-500 on error-bg:       4.5:1 ✓  (badge text)
//   info-500 on info-bg:         4.7:1 ✓  (badge text)
//   focusRing on neutral-0:      4.6:1 ✓  (focus indicator)

export const colors = {
  primary: {
    50: '#f0f7f2',
    100: '#dbeadf',
    200: '#b7d5bf',
    300: '#8db89b',
    400: '#5e9972',
    500: '#3b7c52',
    600: '#2d6040',
    700: '#234c33',
    800: '#1b3a28',
    900: '#132a1c',
  },
  accent: {
    50: '#fff6ed',
    100: '#ffe8d0',
    400: '#ff9a4d',
    500: '#ff7a1f',
    600: '#e36108',
  },
  neutral: {
    0: '#ffffff',
    50: '#fafaf7',
    100: '#f3f3ee',
    200: '#e6e6df',
    300: '#d1d1c8',
    400: '#a6a69c',
    500: '#7a7a70',
    700: '#4a4a43',
    900: '#1c1c18',
  },
  success: { 500: '#2f9e6e', bg: '#e6f5ee' },
  warning: { 500: '#d18b00', bg: '#fff4da' },
  error: { 500: '#c4362f', bg: '#fbe7e5' },
  info: { 500: '#2b6cb0', bg: '#e4efff' },
  focusRing: '#1f7ce0',
  /** Brand-specific colors for third-party visual elements */
  brand: {
    visa: '#1A1F71',
    bankId: '#3B5998',
    klarna: '#FFA8CD', // Klarna's signature soft pink
    klarnaInk: '#17120F', // Klarna's dark text/icon color
  },
  /** Confetti/celebration palette */
  confetti: [
    '#2563eb', // blue
    '#10b981', // emerald
    '#f59e0b', // amber
    '#ef4444', // red
    '#8b5cf6', // violet
    '#ec4899', // pink
  ] as readonly string[],
} as const

export const fontFamilies = {
  sans: "'Inter', system-ui, -apple-system, sans-serif",
  display: "'Fraunces', Georgia, serif",
} as const

export const fontSizes = {
  'display-xl': { size: '3.75rem', lineHeight: '1.05' },
  'display-lg': { size: '3rem', lineHeight: '1.05' },
  'display-md': { size: '2.25rem', lineHeight: '1.1' },
  h1: { size: '1.875rem', lineHeight: '1.15' },
  h2: { size: '1.5rem', lineHeight: '1.2' },
  h3: { size: '1.25rem', lineHeight: '1.3' },
  'body-lg': { size: '1.125rem', lineHeight: '1.55' },
  body: { size: '1rem', lineHeight: '1.55' },
  'body-sm': { size: '0.875rem', lineHeight: '1.5' },
  caption: { size: '0.75rem', lineHeight: '1.4' },
} as const

export const borderRadius = {
  sm: '4px',
  DEFAULT: '8px',
  lg: '12px',
  xl: '20px',
  full: '9999px',
} as const

export const shadows = {
  xs: '0 1px 2px rgba(20, 30, 25, 0.04)',
  sm: '0 2px 6px rgba(20, 30, 25, 0.06)',
  md: '0 6px 18px rgba(20, 30, 25, 0.08)',
  lg: '0 18px 40px rgba(20, 30, 25, 0.12)',
  focus: '0 0 0 3px rgba(31, 124, 224, 0.35)',
} as const

export const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
} as const

/** Media query strings for use with useMediaQuery hook */
export const mediaQueries = {
  sm: `(min-width: ${breakpoints.sm}px)`,
  md: `(min-width: ${breakpoints.md}px)`,
  lg: `(min-width: ${breakpoints.lg}px)`,
  xl: `(min-width: ${breakpoints.xl}px)`,
  mobileOnly: `(max-width: ${breakpoints.md - 1}px)`,
} as const

export const zIndex = {
  dropdown: 20,
  sticky: 30,
  fixed: 40,
  overlay: 50,
  modal: 50,
  toast: 60,
} as const

export const animationDuration = {
  fast: 100,
  normal: 150,
  slow: 200,
  slower: 300,
  slowest: 500,
} as const

/** Convenience re-export as a single namespace */
export const tokens = {
  colors,
  fontFamilies,
  fontSizes,
  borderRadius,
  shadows,
  breakpoints,
  mediaQueries,
  zIndex,
  animationDuration,
} as const
